'use client'

import type { ComponentProps, ReactNode } from 'react'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { SidebarMenuButton, SidebarMenuSubButton } from '@/components/ui/sidebar-menu'
import { GoodUnitedLogo } from '@/components/icons/goodunited-logo'
import { GoodUnitedMark } from '@/components/icons/goodunited-icons'

import {
    AcquireIcon,
    ActionsIcon,
    BuildIcon,
    CalendarDotsIcon,
    CaretDownIcon,
    CaretRightIcon,
    GrowIcon,
    InsightsIcon,
    ListIcon,
    SettingsIcon,
    SupportersIcon
} from '@/components/icons/icon-set'

import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

/*
 * GoodUnited / App layout blocks. Figma: 20 / Blocks / App layout.
 * Shared measurements: 280px sidebar, 72px top bar, 32px page padding, 24px gaps, 12px card corners.
 * Compose AppShell -> PageBody (or WorkspaceBody) -> PageHeader + LayoutColumns -> LayoutContainer.
 * Below 1024px the sidebar becomes a slide-out menu opened from the top bar.
 */

interface NavChild {
    id: string
    label: string
    beta?: boolean
}

interface NavItem {
    id: string
    label: string
    icon: ReactNode
    children?: NavChild[]
}

export const APP_NAV: NavItem[] = [
    { id: 'insights', label: 'Insights', icon: <InsightsIcon /> },
    {
        id: 'actions',
        label: 'Actions',
        icon: <ActionsIcon />,
        children: [
            { id: 'action-inbox', label: 'Action Inbox' },
            { id: 'outcomes', label: 'Outcomes' }
        ]
    },
    {
        id: 'acquire',
        label: 'Acquire',
        icon: <AcquireIcon />,
        children: [
            { id: 'tools', label: 'Tools' },
            { id: 'custom-audiences', label: 'Custom Audiences' }
        ]
    },
    {
        id: 'build',
        label: 'Build',
        icon: <BuildIcon />,
        children: [
            { id: 'my-journeys', label: 'My Journeys' },
            { id: 'journey-library', label: 'Journey Library' }
        ]
    },
    { id: 'grow', label: 'Grow', icon: <GrowIcon /> },
    {
        id: 'supporters',
        label: 'Supporters',
        icon: <SupportersIcon />,
        children: [
            { id: 'all-supporters', label: 'All Supporters' },
            { id: 'supporter-response', label: 'Supporter Response', beta: true },
            { id: 'identity', label: 'Identity' },
            { id: 'lists-and-segments', label: 'List and Segments' }
        ]
    },
    {
        id: 'settings',
        label: 'Settings',
        icon: <SettingsIcon />,
        children: [
            { id: 'admin', label: 'Admin' },
            { id: 'marketing-campaigns', label: 'Marketing Campaigns', beta: true },
            { id: 'ai-context', label: 'AI Context' }
        ]
    }
]

const BetaBadge = () => (
    <Badge variant='success' className='px-1.5 py-0 text-xs'>
        Beta
    </Badge>
)

interface AppSidebarProps extends Omit<ComponentProps<'aside'>, 'children'> {
    activeItem?: string
    showLogo?: boolean
    onNavigate?: (id: string) => void
    /* Replaces the Settings item at the bottom, for example a link back to the docs on demo pages. */
    footer?: ReactNode
}

const groupOf = (id: string) => APP_NAV.find(item => item.id === id || item.children?.some(child => child.id === id))?.id

/*
 * 01 / AppSidebar: the SidebarHeader (logo with an inset divider) and the footer stay put; only the
 * navigation scrolls. Every group starts closed except the one holding the active item.
 */
export function AppSidebar({ activeItem: initialActive = 'insights', showLogo = true, onNavigate, footer, className, ...props }: AppSidebarProps) {
    const [active, setActive] = useState(initialActive)
    const [opened, setOpened] = useState<string[]>(() => {
        const group = groupOf(initialActive)
        return group ? [group] : []
    })

    const select = (id: string) => {
        setActive(id)
        onNavigate?.(id)
    }

    const toggle = (id: string) => setOpened(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id])

    return (
        <aside
            data-slot='app-sidebar'
            aria-label='Main navigation'
            className={cn('flex h-full w-70 shrink-0 flex-col bg-nav-background text-nav-label', className)}
            {...props}
        >
            {showLogo && (
                <div data-slot='sidebar-header' className='relative flex h-18 shrink-0 items-center px-5'>
                    <GoodUnitedLogo id='gu-sidebar-logo' className='h-8 w-auto text-nav-label-hover' />
                    <span className='absolute inset-x-3 bottom-0 h-px bg-nav-divider' aria-hidden='true' />
                </div>
            )}
            <nav className='no-scrollbar flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-3 py-4'>
                {APP_NAV.map(item => {
                    const open = opened.includes(item.id)
                    const groupActive = active === item.id || (item.children?.some(child => child.id === active) ?? false)

                    if (!item.children) {
                        return (
                            <SidebarMenuButton
                                key={item.id}
                                icon={item.icon}
                                label={item.label}
                                active={groupActive}
                                aria-current={groupActive ? 'page' : undefined}
                                onClick={() => select(item.id)}
                            />
                        )
                    }

                    return (
                        <div key={item.id} className='flex shrink-0 flex-col gap-0.5'>
                            <SidebarMenuButton
                                icon={item.icon}
                                label={item.label}
                                active={groupActive}
                                expandable
                                expanded={open}
                                onClick={() => toggle(item.id)}
                            />
                            {open && (
                                <div className='flex shrink-0 flex-col'>
                                    {item.children.map(child => (
                                        <SidebarMenuSubButton
                                            key={child.id}
                                            label={child.label}
                                            active={active === child.id}
                                            badge={child.beta ? <BetaBadge /> : undefined}
                                            onClick={() => select(child.id)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )
                })}
            </nav>
            <div className='shrink-0 border-t border-nav-divider px-3 py-4'>
                {footer ?? <SidebarMenuButton icon={<SettingsIcon />} label='Settings' active={active === 'footer-settings'} onClick={() => select('footer-settings')} />}
            </div>
        </aside>
    )
}

const DATE_RANGES = ['Last 7 days', 'Last 30 days', 'Last Month', 'Last 3 months', 'Last 12 months']

/* The optional date control in the top bar. */
export function TopbarDateControl({ defaultValue = 'Last Month' }: { defaultValue?: string }) {
    const [value, setValue] = useState(defaultValue)

    return (
        <DropdownMenu>
            <DropdownMenuTrigger render={<Button variant='outline' size='sm' />}>
                <CalendarDotsIcon aria-hidden='true' />
                <span className='hidden sm:inline'>
                    {value}
                </span>
                <CaretDownIcon aria-hidden='true' />
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
                <DropdownMenuGroup>
                    <DropdownMenuLabel>
                        Date range
                    </DropdownMenuLabel>
                    {DATE_RANGES.map(range => (
                        <DropdownMenuCheckboxItem key={range} checked={value === range} onCheckedChange={() => setValue(range)}>
                            {range}
                        </DropdownMenuCheckboxItem>
                    ))}
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

/* The account control: avatar with a caret, opening the account menu. */
export function TopbarAccount() {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                aria-label='Account menu'
                className='flex cursor-pointer items-center gap-1 rounded-full text-nav-icon transition-colors outline-none hover:text-nav-icon-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus'
            >
                <Avatar size='lg' className='ring-2 ring-nav-label-hover'>
                    <AvatarFallback />
                </Avatar>
                <CaretDownIcon className='size-4' aria-hidden='true' />
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
                <DropdownMenuGroup>
                    <DropdownMenuItem>
                        Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                        Organization settings
                    </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator className='my-1' />
                <DropdownMenuItem variant='destructive'>
                    Sign out
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

interface AppTopbarProps extends Omit<ComponentProps<'header'>, 'title'> {
    title: ReactNode
    icon?: ReactNode
    showLogo?: boolean
    showDate?: boolean
    actions?: ReactNode
    onMenu?: () => void
}

/*
 * 02 / AppTopbar: starts with a 48px divider in Colors/nav/divider, then the breadcrumb (section icon,
 * page title), the optional date control and the account avatar. 24px side padding, 12px breadcrumb gap.
 * showLogo puts the logo in the bar on small screens, where the sidebar is a slide-out menu.
 */
export function AppTopbar({ title, icon = <InsightsIcon />, showLogo = false, showDate = false, actions, onMenu, className, ...props }: AppTopbarProps) {
    return (
        <header
            data-slot='app-topbar'
            className={cn('flex h-18 shrink-0 items-center bg-topbar-background text-topbar-title', className)}
            {...props}
        >
            {(onMenu || showLogo) && (
                <div className='flex h-full shrink-0 items-center gap-2 pr-4 pl-3 lg:hidden'>
                    {onMenu && (
                        <button
                            type='button'
                            onClick={onMenu}
                            aria-label='Open navigation'
                            className='inline-flex size-10 cursor-pointer items-center justify-center rounded-md text-nav-icon-hover transition-colors outline-none hover:bg-nav-item-active focus-visible:outline-2 focus-visible:outline-border-focus [&_svg]:size-6'
                        >
                            <ListIcon aria-hidden='true' />
                        </button>
                    )}
                    {showLogo && (
                        <>
                            <GoodUnitedLogo id='gu-topbar-logo' className='hidden h-8 w-auto text-nav-label-hover sm:block' />
                            <GoodUnitedMark id='gu-topbar-mark' className='h-7 w-auto text-nav-label-hover sm:hidden' />
                        </>
                    )}
                </div>
            )}
            <span data-slot='app-topbar-divider' className='h-12 w-px shrink-0 bg-nav-divider' aria-hidden='true' />
            <div className='flex min-w-0 flex-1 items-center gap-6 px-4 sm:px-6'>
                <nav aria-label='Breadcrumb' className='flex min-w-0 flex-1 items-center gap-3 text-base font-medium'>
                    <span className='inline-flex shrink-0 text-nav-icon-hover [&_svg]:size-5' aria-hidden='true'>
                        {icon}
                    </span>
                    <CaretRightIcon className='size-4 shrink-0 text-nav-icon' aria-hidden='true' />
                    <span className='truncate' aria-current='page'>
                        {title}
                    </span>
                </nav>
                {(actions || showDate) && (
                    <div className='flex shrink-0 items-center gap-3'>
                        {actions}
                        {showDate && <TopbarDateControl />}
                    </div>
                )}
                <TopbarAccount />
            </div>
        </header>
    )
}

interface PageHeaderProps extends Omit<ComponentProps<'div'>, 'title'> {
    title: ReactNode
    description?: ReactNode
    actions?: ReactNode
}

/* 03 / PageHeader: one shared page heading with an optional description and an Actions slot. */
export function PageHeader({ title, description, actions, className, ...props }: PageHeaderProps) {
    return (
        <div data-slot='page-header' className={cn('flex flex-wrap items-start justify-between gap-4', className)} {...props}>
            <div className='flex min-w-0 flex-col gap-1'>
                <h1 className='font-heading text-3xl font-semibold text-text-primary'>
                    {title}
                </h1>
                {description && (
                    <p className='text-base text-text-secondary'>
                        {description}
                    </p>
                )}
            </div>
            {actions && (
                <div className='flex shrink-0 flex-wrap items-center gap-2'>
                    {actions}
                </div>
            )}
        </div>
    )
}

type ContainerVariant = 'card' | 'panel' | 'lane'

const containerClasses: Record<ContainerVariant, string> = {
    card: 'rounded-xl border border-border-subtle bg-surface-raised p-6',
    panel: 'bg-surface-raised p-6',
    lane: 'rounded-lg bg-surface-muted p-2'
}

/* 04 / Layout/Container: card for dashboards and forms, panel for flush workspaces, lane for boards. */
export function LayoutContainer({ variant = 'card', className, ...props }: ComponentProps<'div'> & { variant?: ContainerVariant }) {
    return (
        <div
            data-slot='layout-container'
            data-variant={variant}
            className={cn('flex min-w-0 flex-col gap-4 text-text-primary', containerClasses[variant], className)}
            {...props}
        />
    )
}

type ColumnTrack = '1' | '2' | '3' | '4' | '2:1' | '1:2'

const trackClasses: Record<ColumnTrack, string> = {
    '1': 'grid-cols-1',
    '2': 'grid-cols-1 md:grid-cols-2',
    '3': 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3',
    '4': 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4',
    '2:1': 'grid-cols-1 lg:grid-cols-[2fr_1fr]',
    '1:2': 'grid-cols-1 lg:grid-cols-[1fr_2fr]'
}

/* 05 / Layout/Columns: tracks 1, 2, 3, 4, 2:1 and 1:2 with 24px gaps. Columns stack on phones. */
export function LayoutColumns({ track = '1', className, ...props }: ComponentProps<'div'> & { track?: ColumnTrack }) {
    return (
        <div
            data-slot='layout-columns'
            data-track={track}
            className={cn('grid gap-6', trackClasses[track], className)}
            {...props}
        />
    )
}

interface PageBodyProps extends Omit<ComponentProps<'div'>, 'title'> {
    title?: ReactNode
    description?: ReactNode
    actions?: ReactNode
}

/* 06 / PageBody: shared 32px spacing with the PageHeader on top and a Content slot. */
export function PageBody({ title, description, actions, className, children, ...props }: PageBodyProps) {
    return (
        <div data-slot='page-body' className={cn('flex min-h-full flex-col gap-6 bg-surface-page p-4 sm:p-6 lg:p-8', className)} {...props}>
            {title && <PageHeader title={title} description={description} actions={actions} />}
            {children}
        </div>
    )
}

/* 06 / WorkspaceBody: fills the page with edge-to-edge panels divided by 1px rules. */
export function WorkspaceBody({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot='workspace-body'
            className={cn('grid min-h-full grid-cols-1 gap-px bg-border-subtle lg:grid-cols-[280px_minmax(0,1fr)_280px] xl:grid-cols-[320px_minmax(0,1fr)_320px]', className)}
            {...props}
        />
    )
}

interface AppShellProps extends Omit<ComponentProps<'div'>, 'title'> {
    title?: ReactNode
    icon?: ReactNode
    activeItem?: string
    showDate?: boolean
    topbarActions?: ReactNode
    /* Passed to AppSidebar as its footer. */
    sidebarFooter?: ReactNode
}

/*
 * 08 / AppShell: the sidebar (with its header) runs full height on the left; the top bar and the page sit
 * beside it. The page is a white surface whose corner against the navigation is rounded with
 * --radius-page (rounded-tl-page) and stays rounded while it scrolls, because the page itself is the
 * scroll container. Fill it with PageBody or WorkspaceBody. Below 1024px the sidebar slides out.
 */
export function AppShell({ title = 'Page title', icon, activeItem, showDate = false, topbarActions, sidebarFooter, className, children, ...props }: AppShellProps) {
    const [menuOpen, setMenuOpen] = useState(false)

    return (
        <div data-slot='app-shell' className={cn('flex h-dvh bg-nav-background', className)} {...props}>
            <AppSidebar activeItem={activeItem} footer={sidebarFooter} className='hidden lg:flex' />
            <div className='flex min-w-0 flex-1 flex-col'>
                <AppTopbar title={title} icon={icon} showLogo showDate={showDate} actions={topbarActions} onMenu={() => setMenuOpen(true)} />
                <main data-slot='app-page' className='relative min-h-0 flex-1 overflow-y-auto rounded-t-page bg-surface-page lg:rounded-tr-none'>
                    {children}
                </main>
            </div>
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
                <SheetContent side='left' className='w-70 border-0 bg-nav-background lg:hidden'>
                    <AppSidebar activeItem={activeItem} footer={sidebarFooter} onNavigate={() => setMenuOpen(false)} className='w-full' />
                </SheetContent>
            </Sheet>
        </div>
    )
}

/* 07 / Composed layout blocks: empty presets built only from containers and column grids. */
export function DashboardLayout({ className }: { className?: string }) {
    return (
        <div className={cn('flex flex-col gap-6', className)}>
            <LayoutColumns track='4'>
                {Array.from({ length: 4 }, (_, index) => <LayoutContainer key={index} className='min-h-36' />)}
            </LayoutColumns>
            <LayoutContainer className='min-h-96' />
            <LayoutColumns track='3'>
                {Array.from({ length: 3 }, (_, index) => <LayoutContainer key={index} className='min-h-72' />)}
            </LayoutColumns>
        </div>
    )
}

export function WorkspaceLayout({ className }: { className?: string }) {
    return (
        <WorkspaceBody className={cn('min-h-160', className)}>
            <LayoutContainer variant='panel' aria-label='List' />
            <LayoutContainer variant='panel' aria-label='Detail' />
            <LayoutContainer variant='panel' aria-label='Context' />
        </WorkspaceBody>
    )
}

export function BoardLayout({ className }: { className?: string }) {
    return (
        <div className={cn('no-scrollbar overflow-x-auto', className)}>
            <div className='grid min-w-220 grid-cols-4 gap-4'>
                {Array.from({ length: 4 }, (_, index) => <LayoutContainer key={index} variant='lane' className='min-h-140' />)}
            </div>
        </div>
    )
}

export type { ColumnTrack, ContainerVariant, NavItem }
